import React from 'react';
import { ConnectionLineComponentProps, getStraightPath, getBezierPath } from 'reactflow';
import { useUIStore } from '../../store';

/**
 * Custom connection line component that shows different styles for execution vs data flow
 * Execution flow (diamond handles): straight line with arrow (theme-aware color)
 * Data flow (round handles): default bezier thin blue line
 */
export const CustomConnectionLine: React.FC<ConnectionLineComponentProps> = ({
  fromX,
  fromY,
  toX,
  toY,
  fromHandle,
  connectionLineStyle,
  fromNode,
}) => {
  const theme = useUIStore((state) => state.theme);
  
  // Check if this is an execution flow connection (from next_node or to previous_node)
  const isExecutionFlow = fromHandle?.id === 'next_node';
  const isLLMOutput = fromHandle?.id === 'llm_output';

  if (isExecutionFlow) {
    // Execution flow: straight line with arrow (theme-aware)
    const [edgePath] = getStraightPath({
      sourceX: fromX,
      sourceY: fromY,
      targetX: toX,
      targetY: toY,
    });

    // Default color based on theme
    const defaultColor = theme === 'dark' ? '#e5e5e5' : '#64748b';
    const strokeColor = connectionLineStyle?.stroke as string || defaultColor;

    return (
      <g>
        <defs>
          <marker
            id="connection-arrow-preview"
            markerWidth="4"
            markerHeight="4"
            refX="3.5"
            refY="2"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="0,0 0,4 4,2"
              fill={strokeColor}
            />
          </marker>
        </defs>
        <path
          fill="none"
          stroke={strokeColor}
          strokeWidth={connectionLineStyle?.strokeWidth || 4}
          className="animated"
          d={edgePath}
          markerEnd="url(#connection-arrow-preview)"
        />
      </g>
    );
  } else if (isLLMOutput) {
    // LLM Output: purple dashed bezier line
    const [edgePath] = getBezierPath({
      sourceX: fromX,
      sourceY: fromY,
      targetX: toX,
      targetY: toY,
    });

    return (
      <g>
        <path
          fill="none"
          stroke="#9333ea"
          strokeWidth={2}
          strokeDasharray="5,5"
          className="animated"
          d={edgePath}
        />
      </g>
    );
  } else {
    // Data flow: type-based color
    const [edgePath] = getBezierPath({
      sourceX: fromX,
      sourceY: fromY,
      targetX: toX,
      targetY: toY,
    });

    // Get output type from source node for type-based coloring
    const outputKey = fromHandle?.id?.replace('output-', '');
    const outputType = fromNode?.data?.output_types?.[outputKey || ''] || 'string';
    const { getArtifactTypeColor } = require('../../utils/artifactTypes');
    const strokeColor = getArtifactTypeColor(outputType);

    return (
      <g>
        <path
          fill="none"
          stroke={strokeColor}  // Type-based color!
          strokeWidth={2}
          className="animated"
          d={edgePath}
        />
      </g>
    );
  }
};

